import {FmlxTable, FmlxIcon} from 'fmlx-common-ui';
import PropTypes from 'prop-types';
import React, {useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import './DetailView.scss';
import {ProtocolExplorerAccordionGroup} from 'constants/protocolExplorerEnum';
import {setProtocolExplorerSort} from 'stores/actions/protocolExplorerAction';
import ProtocolTooltipWrapper from '../ProtocolTooltipWrapper';
import ProtocolStatus from '../ProtocolStatus';
import createSelectableItem from '../../../components/SelectableArea/createSelectableItem';
import useDragDropProtocol from '../useDragDropProtocol';
import {useHybridClicks} from '../../../lib/customHooks';
import {
  getProtocolIcon,
  getExplorerItemDataAttribute,
  EXPLORER_ITEM_CLASSNAME_DETAIL_VIEW,
  isProtocolOwner,
  protocolExplorerHelper,
} from '../../../lib/explorerHelper';
import {getIsOperator, getSessionUser} from '../../../stores/selectors/sessionSelectors';
import ProtocolExplorerFavoriteIndicator from '../ProtocolExplorerFavoriteIndicator';

const COLUMN_DEFINITIONS = [
  {
    Header: 'Name',
    accessor: 'name',
    editable: false,
    headerStyle: {
      width: '45%',
      minWidth: '45%',
      maxWidth: '45%',
    },
  },
  {
    Header: 'Date modified',
    accessor: 'modifiedString',
    disableSort: false,
    headerStyle: {
      width: '20%',
      minWidth: '20%',
      maxWidth: '20%',
    },
  },
  {
    Header: 'Date created',
    accessor: 'createdString',
    disableSort: false,
    headerStyle: {
      width: '20%',
      minWidth: '20%',
      maxWidth: '20%',
    },
  },
  {
    Header: 'Type',
    accessor: 'type',
    editable: false,
    disableSort: true,
    hideSort: true,
    headerStyle: {
      width: '15%',
      minWidth: '15%',
      maxWidth: '15%',
    },
    // eslint-disable-next-line
    Cell: ({ value }) => <span style={{ textTransform: 'capitalize' }}>{value}</span>,
  },
];

const SelectableDetailViewName = createSelectableItem(
  ({
    selectableRef,
    item,
    isRunning,
    greyedOut,
    onBeginDrag,
    onEndDrag,
    onDropped,
    isDragMode,
    isOperatorRoleLoggedIn,
    index,
  }) => {
    const {userId} = useSelector(getSessionUser);
    const protocolOwner = isProtocolOwner(item, isOperatorRoleLoggedIn, userId);
    const isDraggable = protocolExplorerHelper.checkIsItemDraggable(item, userId);
    const isShadowFavorite = item.groupView !== ProtocolExplorerAccordionGroup.FAVORITE_PROTOCOLS;

    const {drag, drop, isDragOver} = useDragDropProtocol({
      protocol: item,
      onBeginDrag,
      onEndDrag,
      onDropped,
      isRunning,
      disableDrag: isOperatorRoleLoggedIn || !isDraggable,
    });
    return (
      <div
        ref={(e) => {
          drag(e);
          drop(e);
          selectableRef(e);
        }}
        className={`
        ${EXPLORER_ITEM_CLASSNAME_DETAIL_VIEW}
        table-name-entry ${greyedOut ? 'greyed-out' : ''} ${
          item.isSelected && isDragMode ? 'dragged' : ''
        } ${isDragOver ? 'drag-over' : ''}`}
        data-id={getExplorerItemDataAttribute(item, index)}
      >
        <ProtocolTooltipWrapper item={item} className="tool-tip">
          <div className="protocol-icon">
            <FmlxIcon
              name={getProtocolIcon(item, isOperatorRoleLoggedIn, protocolOwner)}
              customColor={greyedOut ? '#B4B4B4' : '#414141'}
              size="md"
            />
            <ProtocolStatus protocol={item} isRunning={isRunning} />
          </div>
          <span className="protocol-name-wrapper protocol-name">
            {item.isFavorite && <ProtocolExplorerFavoriteIndicator greyedOut={greyedOut || isShadowFavorite} />}
            {item.name}
          </span>
        </ProtocolTooltipWrapper>
      </div>
    );
  },
);

function DetailView({
  protocols,
  folders,
  favoriteProtocols,
  itemsRef,
  onClickProtocol,
  onDoubleClickProtocol,
  onCheckIsRunning,
  greyedOutProtocolIds,
  onProtocolDropped,
}) {
  const dispatch = useDispatch();

  const protocolsAndFolders = useMemo(
    () => [...favoriteProtocols, ...folders, ...protocols],
    [favoriteProtocols, folders, protocols],
  );

  const isOperatorRoleLoggedIn = useSelector(getIsOperator);

  const [isDragMode, setIsDragMode] = useState(false);

  const handleBeginDrag = (protocol) => {
    if (protocol && !protocol.isSelected) {
      onClickProtocol(protocol);
    }
    setIsDragMode(true);
  };

  const handleEndDrag = () => {
    setIsDragMode(false);
  };

  const selectedRowIds = React.useMemo(
    () => protocolsAndFolders.reduce((indexes, item, idx) => (item.isSelected ? indexes.concat(idx) : indexes), []),
    [protocolsAndFolders],
  );

  const handleRowClick = (rowData) => {
    onClickProtocol(rowData);
  };

  const handleRowDoubleClick = (rowData) => {
    onDoubleClickProtocol(rowData);
  };

  // WORKAROUND: fix onRowDoubleClick issue
  const handleRowEventClicks = useHybridClicks(handleRowClick, handleRowDoubleClick);

  const onChangeSort = ({type, column}) => {
    if (!type || !column) return;

    dispatch(setProtocolExplorerSort({direction: type, accessor: column}));
  };

  return (
    <div className="bootstrap-iso protocol-explorer-detail-view">
      <FmlxTable
        enableDragSelection={false} // Set to false to not use SelectableGroup built in inside FmlxTable
        allowSelectedRow
        showPagination={false}
        columns={COLUMN_DEFINITIONS}
        data={protocolsAndFolders.map((item, index) => ({
          ...item,
          id: index,
          name: (
            <SelectableDetailViewName
              ref={(el) => {
                if (!el?.node) return;

                itemsRef.current[index] = el.node.parentElement.parentElement;
              }}
              index={index}
              item={item}
              isRunning={onCheckIsRunning(item[item.type].id, item.type)}
              trayType={item?.protocol?.trayType}
              greyedOut={greyedOutProtocolIds.includes(item.ID)}
              selectableGroupItem={item}
              onBeginDrag={handleBeginDrag}
              onEndDrag={handleEndDrag}
              onDropped={onProtocolDropped}
              isDragMode={isDragMode}
              isOperatorRoleLoggedIn={isOperatorRoleLoggedIn}
            />
          ),
          modifiedString: protocolExplorerHelper.formatDateString(item.modifiedString),
          createdString: protocolExplorerHelper.formatDateString(item.createdString),
          type: item.type ?? '-',
        }))}
        selectedRowIds={selectedRowIds}
        // onRowClick={handleRowClick}
        // onRowDoubleClick={handleRowDoubleClick}
        // TODO: Use onRowDoubleClick again when it's fixed in fmlx-common-ui
        onRowClick={handleRowEventClicks}
        getSortValue={onChangeSort}
      />
    </div>
  );
}

DetailView.propTypes = {
  protocols: PropTypes.array,
  folders: PropTypes.array,
  favoriteProtocols: PropTypes.array,
  greyedOutProtocolIds: PropTypes.array,
  itemsRef: PropTypes.object,
  onCheckIsRunning: PropTypes.func,
  onClickProtocol: PropTypes.func,
  onDoubleClickProtocol: PropTypes.func,
  onProtocolDropped: PropTypes.func,
};

DetailView.defaultProps = {
  protocols: [],
  folders: [],
  favoriteProtocols: [],
  greyedOutProtocolIds: [],
  itemsRef: {},
  onCheckIsRunning: () => {},
  onClickProtocol: () => {},
  onDoubleClickProtocol: () => {},
  onProtocolDropped: () => {},
};

export default DetailView;
